/**
 * Modified typings for Capture Backend endpoints
 *
 */

import type {
    AccountAttributeDTO,
    AccountInfoDTO,
    AndroidClientRightDTO,
    AppleClientRightDTO,
    AsyncUploadStatusAndChangesResponseDTO,
    ClientGrantorDTO,
    ClientRightDTO,
    ConsentKeyDTO,
    ConsentResponsev2DTO,
    ConsentsResponseDTO,
    DataProtectionUpdateRequestResultDTO,
    DetailedStoryInfoResponseDTO,
    DetailedStoryResponseDTO,
    FileGroupTypeDTO,
    FileObjectResponseDTO,
    FileTypeNameDTO,
    GetRequest3TrashByJobUuidAndFileUuidParams,
    GetRequestStripeProductsParams,
    GrantInfoResponseDTO,
    JobOwnerDTO,
    JobResponseDTO,
    PaymentInfoResponseDTO,
    PostRequestAccountAttributeParams,
    PostRequestJobsParams,
    PurchaseStripeSubscriptionResponseDTO,
    RightPeriodDTO,
    StripeClientRightDTO,
    StripeProductDTO,
    StripeProductDTOSize,
    StripeProductExcludeTrialDTO,
    StripeProductExcludeTrialDTOSize,
    StripeProductListResponseDTO,
    StripeSubscriptionActionRequiredResponseDTO,
    StripeSubscriptionResponseDTO,
    StripeSubscriptionStatusDTO,
    SyncUploadExistsResponseDTO,
    SyncUploadResponseDTO,
    TrashcanFileItemDTO,
} from '@capture/client-api/src/orval'
import type { Merge } from 'type-fest'

export type CommonQueryParams = {
    key: string
    device_id?: string
    client_v?: string
    app_lang?: string // 'no' | 'se' | 'en'
}
// All common query params is required for newer version of client,
// for backward compatibility backend doc keep them optional in CommonQueryParams
export type RequiredCommonQueryParams = Required<CommonQueryParams>
export type RequiredCommonQueryParamsWithAuth = Merge<
    RequiredCommonQueryParams,
    { auth: string }
>
export type ExtraQueryParamsOf<
    T extends CommonQueryParams,
    AdditonalKey extends string = '',
> = Omit<T, keyof CommonQueryParams | AdditonalKey>

export type ExtraJobQueryParamsOf<T extends CommonQueryParams> =
    ExtraQueryParamsOf<T, 'jobUuid'>

export type AccountInfoResponse = Pick<
    AccountInfoDTO,
    | 'is_read_only_user'
    | 'logged_in_as'
    | 'max_space'
    | 'name'
    | 'number_of_subscribed_albums'
    | 'permitted_services'
    | 'profile_picture_url'
    | 'shutdown_info'
    | 'used_space'
    | 'uuid'
    | 'sunset_delete_date'
    | 'has_files_not_media'
> & {
    file_type_counters: NonNullable<AccountInfoDTO['file_type_counters']>
    tos_to_approve?: { link: string; version: string }
}

export type CreateJobProperties = Merge<
    ExtraQueryParamsOf<PostRequestJobsParams>,
    {
        type?: 'story'
    }
>

type SortOrder = 'ADDED' | 'NEWEST_TAKEN' | 'OLDEST_TAKEN'
type ClientJobAttributes = {
    sort_order?: SortOrder
}

type TimelineJobInfoResponse = Merge<
    JobResponseDTO,
    {
        type: undefined
        attributes?: ClientJobAttributes
        owner: JobOwnerDTO
    }
>

export type StoryJobInfoResponse = Merge<
    DetailedStoryInfoResponseDTO,
    {
        type: 'story'
        attributes?: ClientJobAttributes
        size: number
    }
>

export type ShareJobInfoResponse = Merge<
    TimelineJobInfoResponse,
    {
        type: 'share'
    }
>

export type JobInfoResponse =
    | TimelineJobInfoResponse
    | StoryJobInfoResponse
    | ShareJobInfoResponse

export type TimelineJobResponse = Merge<
    JobResponseDTO,
    {
        type: undefined
        size: number
    }
>

export type StoryJobResponse = Merge<
    DetailedStoryResponseDTO,
    {
        attributes?: ClientJobAttributes
        owner: JobOwnerDTO
        type: 'story'
        size: number
        ctime: number
    }
>
export type JobListResponse = (TimelineJobResponse | StoryJobResponse)[]

// Based on observed API-behaviour (as some of the elements are only present for some change-types)
type BaseJobChange = {
    id: string
    path: string
    size: number
    mtime: number
    ctime: number
    timestamp: number
    checksum: string
    serial: number
    type: number
    duration?: number // included for newly uploaded video
    comment_uuid?: string
    user_uuid?: string // Not included when the change is caused by the logged in user.
    comment?: string

    // newly included job changes response properties
    file_type: FileTypeNameDTO
    reaction?: Reaction
    width?: number
    height?: number
    aspect_ratio?: string
    group_type?: FileGroupType
    group_id?: string
    master?: FileMasterSlaveType
    is_rollback?: boolean // not in production yet, TODO(CAPWEB-3274): remove comments here when backend release to production
}

export type CommentedJobChange = BaseJobChange & {
    comment_uuid: string
    comment: string
}

export type ReactionJobChange = BaseJobChange & {
    reaction: Reaction
}

export type JobChange = BaseJobChange | CommentedJobChange | ReactionJobChange

export type FileGroupType = FileGroupTypeDTO
export type FileMasterSlaveType = '1'
export type JobFile = Merge<
    FileObjectResponseDTO,
    {
        group_type?: FileGroupType
        master?: FileMasterSlaveType
        timestamp: number
        is_rollback?: boolean // not in production yet, TODO(CAPWEB-3274): remove comments here when backend release to production
    }
>

export type DeletedFile = Merge<
    TrashcanFileItemDTO,
    {
        group_type?: FileGroupType
        master?: FileMasterSlaveType
    }
>

export type DeletedFileResponse = {
    deletedFiles: DeletedFile[]
    totalItemCount: number
}

export type Reaction = '' | 'love'

export type StripeUserGrant = Merge<
    StripeClientRightDTO,
    {
        source: ClientGrantorDTO.capture_stripe
        period: RightPeriodDTO.monthly | RightPeriodDTO.yearly
    }
>

type AppleStoreUserGrant = Merge<
    AppleClientRightDTO,
    {
        source: ClientGrantorDTO.apple_store
    }
>

type AndroidStoreUserGrant = Merge<
    AndroidClientRightDTO,
    {
        source: ClientGrantorDTO.android_store
    }
>

type OtherUserGrant = Merge<
    ClientRightDTO,
    {
        source:
            | ClientGrantorDTO.capture
            | ClientGrantorDTO.b2b
            | ClientGrantorDTO.customer_service
            | ClientGrantorDTO.other
    }
>

export type UserGrant =
    | StripeUserGrant
    | AppleStoreUserGrant
    | AndroidStoreUserGrant
    | OtherUserGrant

export type GrantInfoResponse = Merge<
    GrantInfoResponseDTO,
    {
        result: { grants: UserGrant[] }
    }
>

export type StripeProductsMode =
    keyof ExtraQueryParamsOf<GetRequestStripeProductsParams>

/*
 * Stripe Product Type
 * example:
 * {
 *      "id": "plan_",
 *      "period": "monthly",
 *      "size": 5000,  // number in GB
 *      "price": 9900,
 *      "currency": "NOK",
 *      "env": "test"
 * }
 * */
export type StripeProduct = Merge<
    StripeProductDTO,
    {
        // TODO(br1anchen): remove this when orval v7.8.0 is released (https://github.com/orval-labs/orval/pull/1957)
        size: StripeProductDTOSize
        period: RightPeriodDTO.monthly | RightPeriodDTO.yearly
        price: number
    }
>

/*
 * Stripe Products Response Type
 * example:
 * {
 *      "result": [],
 *      "trial_period_months": 6, // 0 if user is not eligible for trial
 *      "unavailable_product_ids": [],
 * }
 * */
export type StripeProductsResponse = Merge<
    StripeProductListResponseDTO,
    {
        result: StripeProduct[]
    }
>

type StripePurchaseOtherResponse = Merge<
    PurchaseStripeSubscriptionResponseDTO,
    {
        status:
            | StripeSubscriptionStatusDTO.incomplete_expired
            | StripeSubscriptionStatusDTO.trialing
            | StripeSubscriptionStatusDTO.active
            | StripeSubscriptionStatusDTO.past_due
            | StripeSubscriptionStatusDTO.unpaid
            | StripeSubscriptionStatusDTO.paused
            | StripeSubscriptionStatusDTO.SubNotFoundInStripe
    }
>
type StripePurchaseIncompleteResponse = Merge<
    StripeSubscriptionActionRequiredResponseDTO,
    {
        status: StripeSubscriptionStatusDTO.incomplete
    }
>
export type StripePurchaseResponse =
    | StripePurchaseOtherResponse
    | StripePurchaseIncompleteResponse

export type StripePaymentInfo = PaymentInfoResponseDTO

export type UpcomingStripeProduct = Merge<
    StripeProductExcludeTrialDTO,
    {
        price: number
        period: RightPeriodDTO.monthly | RightPeriodDTO.yearly
        start_at: string
        // TODO(br1anchen): remove this when orval v7.8.0 is released (https://github.com/orval-labs/orval/pull/1957)
        size: StripeProductExcludeTrialDTOSize
    }
>
export type ActiveStripeProduct = Merge<
    StripeProductExcludeTrialDTO,
    {
        price: number
        period: RightPeriodDTO.monthly | RightPeriodDTO.yearly
        end_at: string
        // TODO(br1anchen): remove this when orval v7.8.0 is released (https://github.com/orval-labs/orval/pull/1957)
        size: StripeProductExcludeTrialDTOSize
    }
>

export type StripeSubscriptionResponse = Merge<
    StripeSubscriptionResponseDTO,
    {
        products: (UpcomingStripeProduct | ActiveStripeProduct)[]
    }
>

export type ClientStats = {
    last_foreground: number
    last_engaged: number
}

export type MigratedDataProtectionConsentData = Record<
    Exclude<ConsentKeyDTO, ConsentKeyDTO.facial_recognition>,
    boolean
>
export type UnMigratedDataProtectionConsentData = Record<
    Exclude<
        ConsentKeyDTO,
        | ConsentKeyDTO.face_grouping
        | ConsentKeyDTO.advanced_filtering_and_categorization
    >,
    boolean
>
export type DataProtectionConsentData =
    | MigratedDataProtectionConsentData
    | UnMigratedDataProtectionConsentData
export type DataProtectionConsentsResponse = Merge<
    ConsentsResponseDTO,
    {
        data: DataProtectionConsentData
    }
>

export type DataProtectionUpdateRequestResult = Merge<
    DataProtectionUpdateRequestResultDTO,
    {
        consents: Record<ConsentKeyDTO, ConsentResponsev2DTO>
    }
>

export type UserAccountAttributeValue = {
    album_list_sort:
        | 'RECENT_ACTIVITY'
        | 'NEWEST_CREATED'
        | 'OLDEST_CREATED'
        | 'NAME'
}
export type UserAccountAttributeKey = keyof UserAccountAttributeValue
export type PostAccountAttributeAccountAttributePostParams = Merge<
    ExtraQueryParamsOf<PostRequestAccountAttributeParams>,
    {
        account_attribute_key: UserAccountAttributeKey
    }
>
export type UpsertUserAccountAttribute<
    T extends UserAccountAttributeKey = UserAccountAttributeKey,
> = {
    account_attribute_key: T
    value: UserAccountAttributeValue[T]
}

export type UserAccountAttribute<
    T extends UserAccountAttributeKey = UserAccountAttributeKey,
> = Merge<
    AccountAttributeDTO,
    {
        key: T
        value: UserAccountAttributeValue[T]
    }
>

/**  Async upload flow
 *
 *        CAPBAKAsyncUploadState.Uploading            CAPBAKAsyncUploadState.Processing
 *   User --------------------------------> Backend ---------------------------------> Amazon
 *
 */

export type ChangesAndAsyncUploadStatus = Merge<
    AsyncUploadStatusAndChangesResponseDTO,
    {
        changes: JobChange[]
        info: NonNullable<AsyncUploadStatusAndChangesResponseDTO['info']>
    }
>

export type UploadResponse = SyncUploadExistsResponseDTO | SyncUploadResponseDTO

export enum ThumbArea {
    SMALL = 256,
    LARGE = 1280,
}

export type TrashByJobUuidAndFileUuidParams = Merge<
    ExtraQueryParamsOf<GetRequest3TrashByJobUuidAndFileUuidParams>,
    {
        area?: ThumbArea
    }
>
